/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { getAuth } from 'firebase/auth';

interface CollectionColors {
  backgroundColor: string;
  textColor: string;
  buttonColor: string;
  accentColor: string;
  h1Color: string;
}

const DEFAULT_COLORS: CollectionColors = {
  backgroundColor: '#2a2a2a',
  textColor: '#FFFFFF',
  buttonColor: '#4CAF50',
  accentColor: '#3fb8cc',
  h1Color: '#FFFFFF'
};

interface CollectionColorEditorProps {
  collectionHandle: string;
  initialColors?: CollectionColors;
  onUpdate?: () => void;
}


const CollectionColorEditor: React.FC<CollectionColorEditorProps> = ({
  collectionHandle,
  initialColors,
  onUpdate
}) => {
  const [colors, setColors] = useState<CollectionColors>(initialColors || DEFAULT_COLORS);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Sync internal state when initialColors changes
  useEffect(() => {
    if (initialColors) {
      setColors(initialColors);
    }
  }, [initialColors]);

  const handleColorChange = (key: keyof CollectionColors, value: string) => {
    setColors(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const auth = getAuth();
      const user = auth.currentUser;
      
      if (!user) {
        throw new Error('Not authenticated');
      }

      const token = await user.getIdToken();

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/update-collection-colors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          collectionHandle,
          colors,
          token,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to update colors');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      
      if (onUpdate) {
        onUpdate();
      }
    } catch (err: any) {
      setError(err.message || 'Failed to update colors');
    } finally {
      setSaving(false);
    }
  };

  const hasChanges = JSON.stringify(colors) !== JSON.stringify(initialColors || DEFAULT_COLORS);

  return (
    <div className="editor-section">
      <h3>Collection Colors</h3>
      <div className="color-picker-grid">
        <div className="color-picker-item">
          <label htmlFor="backgroundColor">Background Color</label>
          <div className="color-input-wrapper">
            <input 
              type="color" 
              id="backgroundColor"
              value={colors.backgroundColor}
              onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
              disabled={saving}
              className="color-input"
            />
            <input 
              type="text" 
              value={colors.backgroundColor}
              onChange={(e) => handleColorChange('backgroundColor', e.target.value)}
              disabled={saving}
              className="color-text-input"
              placeholder="#FFFFFF"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        </div>

        <div className="color-picker-item">
          <label htmlFor="textColor">Text Color</label>
          <div className="color-input-wrapper">
            <input 
              type="color" 
              id="textColor"
              value={colors.textColor}
              onChange={(e) => handleColorChange('textColor', e.target.value)}
              disabled={saving}
              className="color-input"
            />
            <input 
              type="text" 
              value={colors.textColor}
              onChange={(e) => handleColorChange('textColor', e.target.value)}
              disabled={saving}
              className="color-text-input"
              placeholder="#000000"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        </div>

        <div className="color-picker-item">
          <label htmlFor="h1Color">Title (H1) Color</label>
          <div className="color-input-wrapper">
            <input 
              type="color" 
              id="h1Color"
              value={colors.h1Color}
              onChange={(e) => handleColorChange('h1Color', e.target.value)}
              disabled={saving}
              className="color-input"
            />
            <input 
              type="text" 
              value={colors.h1Color}
              onChange={(e) => handleColorChange('h1Color', e.target.value)}
              disabled={saving}
              className="color-text-input"
              placeholder="#000000"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        </div>

        <div className="color-picker-item">
          <label htmlFor="buttonColor">Button Color</label>
          <div className="color-input-wrapper">
            <input 
              type="color" 
              id="buttonColor"
              value={colors.buttonColor}
              onChange={(e) => handleColorChange('buttonColor', e.target.value)}
              disabled={saving}
              className="color-input"
            />
            <input 
              type="text" 
              value={colors.buttonColor}
              onChange={(e) => handleColorChange('buttonColor', e.target.value)}
              disabled={saving}
              className="color-text-input"
              placeholder="#4CAF50"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        </div>

        <div className="color-picker-item">
          <label htmlFor="accentColor">Accent Color</label>
          <div className="color-input-wrapper">
            <input 
              type="color" 
              id="accentColor"
              value={colors.accentColor}
              onChange={(e) => handleColorChange('accentColor', e.target.value)}
              disabled={saving}
              className="color-input"
            />
            <input 
              type="text" 
              value={colors.accentColor}
              onChange={(e) => handleColorChange('accentColor', e.target.value)}
              disabled={saving}
              className="color-text-input"
              placeholder="#2196F3"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        </div>
      </div>

      <div className="color-preview">
        <h4>Preview:</h4>
        <div className="preview-box" style={{ 
          backgroundColor: colors.backgroundColor,
          color: colors.textColor,
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #ddd'
        }}>
         <h3 style={{ color: colors.h1Color }}>Collection Title</h3>
          <p style={{ color: colors.textColor }}>This is sample text to preview your colors.</p>
          <button style={{ 
            backgroundColor: colors.buttonColor, 
            color: '#fff',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '4px',
            cursor: 'pointer'
          }}>
            Sample Button
          </button>
          <div style={{ 
            marginTop: '10px',
            padding: '10px',
            backgroundColor: colors.accentColor,
            color: '#fff',
            borderRadius: '4px'
          }}>
            Accent Element
          </div>
        </div>
      </div>
      
      <button 
        onClick={handleSave} 
        disabled={saving || !hasChanges}
        className="save-btn"
      >
        {saving ? 'Saving...' : 'Save Colors'}
      </button>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">✓ Colors updated successfully!</div>}
    </div>
  );
};

export default CollectionColorEditor;